import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle2, 
  Calendar, 
  Clock, 
  MapPin, 
  Copy, 
  Home, 
  ArrowLeft,
  Mail,
  FileText,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

// Fallback copy function for when Clipboard API is blocked
const copyToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
    }
  }
  
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    document.body.removeChild(textArea);
    return false;
  }
};

const BookingConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingData = location.state;

  // If no booking data, redirect to calendar
  if (!bookingData) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <Card className="glass border-white/10 max-w-md w-full">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-cameroon-yellow mx-auto mb-4" />
              <h1 className="font-serif text-2xl font-bold text-white mb-4">
                No Booking Found
              </h1>
              <p className="text-white/70 mb-6">
                It looks like you arrived here without completing a booking. Please submit a new event request.
              </p>
              <Link to="/calendar">
                <Button className="bg-cameroon-green hover:bg-cameroon-green/90 w-full">
                  <Calendar className="w-4 h-4 mr-2" />
                  Go to Calendar
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const {
    confirmationNumber,
    title,
    date,
    endDate,
    time,
    endTime,
    location: eventLocation,
    category,
    organizationName,
    submitterEmail
  } = bookingData;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateRange = () => {
    if (!date) return '';
    const start = formatDate(date);
    if (!endDate || endDate === date) return start;
    const end = formatDate(endDate);
    return `${start} - ${end}`;
  };

  const copyConfirmationNumber = async () => {
    const success = await copyToClipboard(confirmationNumber);
    if (success) {
      toast.success('Confirmation number copied to clipboard!');
    } else {
      toast.error('Could not copy. Please select and copy manually.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <Navbar />
      
      <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-2xl">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-cameroon-green/20 mb-4">
              <CheckCircle2 className="w-12 h-12 text-cameroon-green" />
            </div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-2">
              Booking Request Received!
            </h1>
            <p className="text-white/70 text-sm sm:text-base">
              Your event has been submitted and is pending approval
            </p>
          </div>

          {/* Confirmation Number Card */}
          <Card className="bg-gradient-to-br from-cameroon-green to-cameroon-green/80 border-0 mb-6">
            <CardContent className="p-6 sm:p-8 text-center text-white">
              <p className="text-sm uppercase tracking-widest opacity-80 mb-2">
                Confirmation Number
              </p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-2xl sm:text-4xl font-bold font-mono tracking-wider">
                  {confirmationNumber}
                </span>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={copyConfirmationNumber}
                  data-testid="copy-confirmation-btn"
                >
                  <Copy className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-xs mt-3 opacity-70">
                Save this number to reference your booking with CCA Admin
              </p>
            </CardContent>
          </Card>

          {/* Status Badge */}
          <div className="flex justify-center mb-6">
            <Badge className="bg-cameroon-yellow/20 text-cameroon-yellow border border-cameroon-yellow/30 px-4 py-2 text-sm">
              <Clock className="w-4 h-4 mr-2" />
              Status: Pending Review
            </Badge>
          </div>

          {/* Event Details Card */}
          <Card className="glass border-white/10 mb-6">
            <CardContent className="p-6">
              <h2 className="font-semibold text-white text-lg mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-cameroon-green" />
                Event Details
              </h2>
              
              <div className="space-y-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="text-xl font-semibold text-white mb-1">{title}</h3>
                  <Badge variant="outline" className="text-xs text-cameroon-yellow border-cameroon-yellow/30">
                    {category}
                  </Badge>
                </div>

                <div className="grid gap-3">
                  <div className="flex items-start gap-3 text-white/80">
                    <Calendar className="w-5 h-5 text-cameroon-green flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-white/50 uppercase tracking-wide">Date</p>
                      <p className="font-medium">{formatDateRange()}</p>
                      {endDate && endDate !== date && (
                        <Badge variant="secondary" className="mt-1 text-xs bg-white/10">
                          Multi-day Event
                        </Badge>
                      )}
                    </div>
                  </div>

                  {time && (
                    <div className="flex items-start gap-3 text-white/80">
                      <Clock className="w-5 h-5 text-cameroon-yellow flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-white/50 uppercase tracking-wide">Time</p>
                        <p className="font-medium">{time}{endTime && ` - ${endTime}`}</p>
                      </div>
                    </div>
                  )}

                  {eventLocation && (
                    <div className="flex items-start gap-3 text-white/80">
                      <MapPin className="w-5 h-5 text-cameroon-red flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-white/50 uppercase tracking-wide">Location</p>
                        <p className="font-medium">{eventLocation}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3 text-white/80">
                    <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-white/50 uppercase tracking-wide">Organization</p>
                      <p className="font-medium">{organizationName}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What's Next Card */}
          <Card className="bg-cameroon-yellow/10 border-cameroon-yellow/30 mb-6">
            <CardContent className="p-6">
              <h2 className="font-semibold text-white text-lg mb-4">
                What Happens Next?
              </h2>
              <ol className="space-y-3 text-white/80 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cameroon-green text-white text-xs flex items-center justify-center font-bold">1</span>
                  <span>Our admin team will review your event request within 1-2 business days</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cameroon-yellow text-black text-xs flex items-center justify-center font-bold">2</span>
                  <span>You will receive an email notification once your event is approved or if we need more information</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cameroon-red text-white text-xs flex items-center justify-center font-bold">3</span>
                  <span>Once approved, your event will appear on the public calendar and the date will be blocked</span>
                </li>
              </ol>
              
              {submitterEmail && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-sm text-white/60">
                    A confirmation email has been sent to <span className="text-cameroon-green font-medium">{submitterEmail}</span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="glass border-white/10 mb-6">
            <CardContent className="p-6">
              <h2 className="font-semibold text-white text-lg mb-3">
                Need to Make Changes?
              </h2>
              <p className="text-white/70 text-sm mb-3">
                Contact our admin team with your confirmation number:
              </p>
              <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-white font-mono text-lg">{confirmationNumber}</p>
                  <p className="text-xs text-white/50">Reference this when contacting us</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10"
                  onClick={copyConfirmationNumber}
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              className="flex-1 border-white/20 text-white hover:bg-white/10"
              onClick={() => navigate('/calendar')}
              data-testid="back-to-calendar-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Calendar
            </Button>
            <Link to={`/track-booking?ref=${confirmationNumber}`} className="flex-1">
              <Button 
                variant="outline"
                className="w-full border-cameroon-green/50 text-cameroon-green hover:bg-cameroon-green/10"
                data-testid="track-booking-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Track Status
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button 
                className="w-full bg-cameroon-green hover:bg-cameroon-green/90"
                data-testid="go-home-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Home
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Council of Cultural Associations Montreal
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookingConfirmationPage;
